<template>
    <div>
        <pc-label
            v-bind:label="itemsColumnHeader"
            bold
            class="d-sm-none" />
        <pc-table
            id="ItemTable"
            ref="itemTable"
            v-bind:items="tableItems"
            v-bind:columns="columns"
            key-prop="PublicId"
            hide-search
            card-hide-sort
            card-hide-headers
            hide-card-horizontal-separator>
            <template v-slot:Name="row">
                <div class="d-flex my-1">
                    <envelope-icon
                        style="display: inline-block"
                        class="me-2"
                        v-bind:type="getItemIconType(row)"
                        v-bind:center="false" />
                    <pc-label
                        v-bind:label="getItemName(row)"
                        v-bind:show-required="row.IsRequired" />
                </div>
            </template>
            <template v-slot:Actions="row">
                <div class="my-1">
                    <envelope-item-definition-actions
                        v-if="showActions(row)"
                        v-bind:item-definition="row" />
                    <div v-if="showMissingAttachmentItem(row)">
                        <span
                            v-if="row.IsRequired"
                            class="text-danger fst-italic">
                            {{ missingRequiredAttachmentItemText }}
                        </span>
                        <span
                            v-else
                            class="text-body-secondary fst-italic">
                            {{ missingOptionalAttachmentItemText }}
                        </span>
                    </div>
                    <wet-sign-item
                        v-if="showWetSignControl"
                        v-bind:envelope-id="envelope.EnvelopeId"
                        v-bind:item="getItem(row)"
                        v-bind:item-definition="row"
                        v-bind:discardable="showDiscard" />
                </div>
            </template>
        </pc-table>
    </div>
</template>

<script>
    import Vuex from "vuex";
    import { ENVELOPE_ITEM_TYPE } from "ESignatures/EditEnvelope/editEnvelopeStore";
    import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
    import envelopeIcon, { getIconTypeForItemType } from "ESignatures/EditEnvelope/Components/envelopeIcon.vue";
    import { envelopeElementMixin } from "./envelopeElementMixin";
    import wetSignItem from "./wetSignEnvelopeItem.vue";
    import envelopeItemDefinitionActions from "./envelopeItemDefinitionActions.vue";

    export default {
        components: {
            envelopeIcon,
            wetSignItem,
            envelopeItemDefinitionActions
        },
        mixins: [envelopeElementMixin],
        inject: {
            readonly: {
                from: "readonly",
                default: false
            }
        },
        props: {
            envelope: { type: Object, required: false, default: null },
            envelopeDefinition: { type: Object, required: true }
        },
        data: function () {
            return {};
        },
        computed: {
            tableItems() {
                if (!this.envelopeElement_hasEnvelope) {
                    return this.envelopeElement_orderedItemDefinitions;
                }

                // For an in-progress envelope, only show item definitions
                // where the item is present
                return this.envelopeElement_orderedItemDefinitions.filter((eid) => {
                    return this.envelopeElement_itemIsPresentByItemDefinitionId(eid.PublicId);
                });
            },
            columns() {
                return [
                    {
                        header: this.itemsColumnHeader,
                        property: "Name",
                        visible: true,
                        searchable: false,
                        orderable: false,
                        sortValue: function (item) {
                            return item.Order;
                        }
                    },
                    {
                        header: this.actionsColumnHeader,
                        property: "Actions",
                        visible: this.anyActions
                    }
                ];
            },
            anyActions() {
                return this.showWetSignControl || !this.envelopeElement_hasEnvelope;
            },
            hasWetSignEnvelope() {
                return this.envelopeElement_hasEnvelope && this.envelope.IsWetSign;
            },
            showWetSignControl() {
                return this.hasWetSignEnvelope && !this.envelopeElement_isStatusTerminal;
            },
            showDiscard() {
                return this.envelopeElement_hasEnvelope && !this.envelopeElement_isStatusTerminal;
            },
            ...Vuex.mapGetters({
                getGeneratedDocumentTemplateById: PROGRAM_TYPES.getters.getGeneratedDocumentTemplateById,
                attachmentDefinitionFromId: PROGRAM_TYPES.getters.attachmentDefinitionFromId,
                getContentLibraryItemById: PROGRAM_TYPES.getters.getContentLibraryItemById
            })
        },
        methods: {
            getItemIconType(itemDefinition) {
                return getIconTypeForItemType(itemDefinition.Type);
            },
            getItemName(itemDefinition) {
                if (itemDefinition.Label) {
                    return itemDefinition.Label;
                }
                switch (itemDefinition.Type) {
                    case ENVELOPE_ITEM_TYPE.Attachment:
                        return this.attachmentDefinitionFromId(itemDefinition.AttachmentDefinitionId).Name;
                    case ENVELOPE_ITEM_TYPE.ContentItem:
                        return this.getContentLibraryItemById(itemDefinition.ContentItemId).Name;
                    case ENVELOPE_ITEM_TYPE.DocumentTemplate:
                        return this.getGeneratedDocumentTemplateById(itemDefinition.DocumentTemplateDefinitionId).Name;
                    default:
                        throw `Unrecognized item type ${this.type}`;
                }
            },
            showActions(itemDefinition) {
                return !this.envelopeElement_hasEnvelope && !this.envelopeElement_missingAttachment(itemDefinition);
            },
            showMissingAttachmentItem(itemDefinition) {
                return !this.envelopeElement_hasEnvelope && this.envelopeElement_missingAttachment(itemDefinition);
            },
            itemIsDocumentTemplate(itemDefinition) {
                return itemDefinition.Type === ENVELOPE_ITEM_TYPE.DocumentTemplate;
            },
            getItem(itemDefinition) {
                return this.envelopeElement_itemsByDefinitionId[itemDefinition.PublicId];
            }
        }
    };
</script>
